import { cn, IconButton, RiArrowLeftLine, Stack } from 'component-library';
import React, { FC, PropsWithChildren } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { OrganizationSearchBar } from '@/components/SearchBar/SearchBar';
import { ButtonWidget, ButtonWidgetProps } from '@/components/widgets/a/Button/Button';
import { usePlotGroupFiltersConfiguration, usePlotGroupFiltersManagementPanel } from '@/components/widgets/config';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useUIStyle } from '@/hooks/useUIStyle';
import { WorkstreamPill } from '@/pages/shared/home/components/WorkstreamPill';

import { Icon } from '../../a/Icon/Icon';

type SecondaryNavPropsBase = {
  /** The title of the SecondaryNav element. */
  title: React.ReactNode | string;
  actionButton?: ButtonWidgetProps;
  showSearchBar?: boolean;
  showWorkstreamPill?: boolean;
  customSearchBar?: React.ReactNode;
  showFilters?: boolean;
  canEditFilters?: boolean;
  className?: string;
  children?: React.ReactNode;
  onGoToCustomizeLayout?: () => void;
};

type SecondaryNavPropsWithBackPath = SecondaryNavPropsBase & {
  /** The path the back icon will link to. */
  backPath?: string;
};

type SecondaryNavPropsWithBackAction = SecondaryNavPropsBase & {
  /** The callback function to invoke when the back icon is clicked. */
  backAction?: () => void;
};

export type SecondaryNavProps = SecondaryNavPropsWithBackPath | SecondaryNavPropsWithBackAction;

export const NavBar: FC<PropsWithChildren<SecondaryNavProps>> = ({
  title,
  actionButton,
  showSearchBar = true,
  customSearchBar,
  showFilters,
  canEditFilters = true,
  className,
  children,
  showWorkstreamPill = true,
  onGoToCustomizeLayout,
  ...rest
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const restProps = rest as any;
  const { state } = useLocation();

  const isStaffUser = useIsStaffUser();
  const hasBackButton = restProps.backPath || restProps.backAction;

  return (
    <Stack
      direction='col'
      spacing={4}
      className={cn('min-h-[64px]', className, showFilters === false && 'pb-8')}
      data-testid='secondary-nav'
    >
      <Stack direction='row' spacing={4} center className='w-full justify-between'>
        <Stack direction='row' spacing={2} className='justify-start'>
          {hasBackButton && (
            <SecondaryNavBackButton
              href={state?.previousPath ?? state?.from ?? restProps.backPath}
              onClick={restProps.backAction}
            />
          )}
          {isStaffUser && onGoToCustomizeLayout && (
            <ButtonWidget variant='outline' className='w-8 aspect-auto' onClick={onGoToCustomizeLayout}>
              <Icon icon='ri-layout-4-line' />
            </ButtonWidget>
          )}
          <span className='typography-h2 text-text-primary truncate max-w-screen-sm'>{title}</span>
        </Stack>
        {showSearchBar && (customSearchBar || <OrganizationSearchBar />)}
        {showWorkstreamPill && <WorkstreamPill />}
        <Stack direction='row' spacing={4} center>
          {children}
          {actionButton && <ActionButton {...actionButton} />}
          {showFilters && canEditFilters && <PlotGroupFilters />}
        </Stack>
      </Stack>
      {showFilters && <PlotGroupFiltersManagementPanel canEditFilters={canEditFilters} />}
    </Stack>
  );
};

const SecondaryNavBackButton: FC<{ href?: string; onClick?: () => void }> = ({ href, onClick }) => {
  const icon = <RiArrowLeftLine className='text-text-secondary' size={24} />;

  return (
    <IconButton onClick={onClick} data-testid='secondary-nav-back-button' asChild={!!href}>
      {href ? (
        <Link role='link' data-testid='back-link' to={href}>
          {icon}
        </Link>
      ) : (
        icon
      )}
    </IconButton>
  );
};

const PlotGroupFilters = () => {
  const canShowFilters = !useUIStyle(OrganizationUIStyle.CONSERVATION);
  const filterWidgetConfiguration = usePlotGroupFiltersConfiguration();

  if (!canShowFilters) {
    return null;
  }
  return <WidgetSlotRenderer nestedWidget={filterWidgetConfiguration} />;
};

const PlotGroupFiltersManagementPanel = ({ canEditFilters }: { canEditFilters: boolean }) => {
  const canShowFilters = !useUIStyle(OrganizationUIStyle.CONSERVATION);
  const managementPanelConfiguration = usePlotGroupFiltersManagementPanel({
    canEditFilters,
  });
  if (!canShowFilters) {
    return null;
  }
  return <WidgetSlotRenderer nestedWidget={managementPanelConfiguration} />;
};

const ActionButton = ({ ...delegated }) => {
  return <ButtonWidget variant='outline' className={cn('w-fit', delegated?.className)} {...delegated} />;
};
